import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Field, TextInput, TextArea } from "@/components/Field";
import { Reveal, PrimaryButton } from "@/components/primitives";
import Snippet from "@/components/Snippet";
import { api, formatApiError } from "@/lib/api";
import { PRACTITIONER, LIB } from "@/data/content";
import { useSite } from "@/context/SiteContext";

export default function Contact() {
  const { page, img, settings } = useSite();
  const hero = page("contact").hero || {};
  const email = settings.email || PRACTITIONER.email;
  const emailDisplay = settings.email_display || PRACTITIONER.emailDisplay || email;
  const phone = settings.phone || PRACTITIONER.phone;
  const location = settings.location || PRACTITIONER.location;
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", form);
      setDone(true);
      toast.success("Thank you — your message is on its way to Isabelle.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send your message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHero
        eyebrow={hero.eyebrow || "Say hello"}
        script={hero.script || "Get in"}
        title={hero.title || "Touch with Isabelle"}
        intro={hero.intro || "Have a question, or simply want to know more before you decide? Send a gentle note and Isabelle will reply with care."}
        image={img("contact_hero", LIB.coastCliffs)}
        imageAlt="South Devon cliffs meeting a calm sea"
      />

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-14 items-start">
          <Reveal className="lg:col-span-2">
            <h2 className="font-heading text-3xl text-brand-ink mb-6"><Snippet k="contact.side_heading" fallback="Other ways to reach me" /></h2>
            <ul className="space-y-5 font-body text-brand-slate">
              <li className="flex items-center gap-4"><span className="h-11 w-11 rounded-full bg-brand-lavender-soft flex items-center justify-center"><Mail className="text-brand-lavender-dark" size={18} strokeWidth={1.5} /></span><a href={`mailto:${email}`} className="hover:text-brand-forest transition-colors">{emailDisplay}</a></li>
              <li className="flex items-center gap-4"><span className="h-11 w-11 rounded-full bg-brand-lavender-soft flex items-center justify-center"><Phone className="text-brand-lavender-dark" size={18} strokeWidth={1.5} /></span>{phone}</li>
              <li className="flex items-center gap-4"><span className="h-11 w-11 rounded-full bg-brand-lavender-soft flex items-center justify-center"><MapPin className="text-brand-lavender-dark" size={18} strokeWidth={1.5} /></span>{location}</li>
            </ul>
            <p className="mt-8 font-body text-sm text-brand-slate/80 leading-relaxed">
              Ready to experience a session? You can also{" "}
              <Link to="/book" className="text-brand-forest underline underline-offset-4">request a booking</Link> directly.
            </p>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3">
            <div className="rounded-3xl border border-brand-line bg-white p-8 md:p-10">
              {done ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle2 className="mx-auto text-brand-forest mb-4" size={48} strokeWidth={1.25} />
                  <h3 className="font-heading text-3xl text-brand-ink mb-2"><Snippet k="contact.success_heading" fallback="Message sent" /></h3>
                  <p className="font-body text-brand-slate"><Snippet k="contact.success_text" fallback="Thank you for reaching out. Isabelle will be in touch soon." /></p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-7">
                    <Field label="Your name" required>
                      <TextInput data-testid="contact-name" value={form.name} onChange={upd("name")} placeholder="Jane" />
                    </Field>
                    <Field label="Email" required>
                      <TextInput data-testid="contact-email" type="email" value={form.email} onChange={upd("email")} placeholder="jane@email.com" />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-7">
                    <Field label="Phone (optional)">
                      <TextInput data-testid="contact-phone" value={form.phone} onChange={upd("phone")} placeholder="07700 900000" />
                    </Field>
                    <Field label="Subject (optional)">
                      <TextInput data-testid="contact-subject" value={form.subject} onChange={upd("subject")} placeholder="A quick question" />
                    </Field>
                  </div>
                  <Field label="Your message" required>
                    <TextArea data-testid="contact-message" rows={5} value={form.message} onChange={upd("message")} placeholder="How can I help?" />
                  </Field>
                  <PrimaryButton type="submit" disabled={sending} data-testid="contact-submit" className="disabled:opacity-60">
                    {sending ? "Sending…" : <Snippet k="contact.submit" fallback="Send message" />}
                  </PrimaryButton>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
